import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, CircleAlert, IdCard, ScanBarcode, Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { alertsFromError, homeLocationNote, type BorrowerAlert, type HomeLocationResult } from '../lib/checkouts';
import { addDays, ASSET_STATUS_LABEL, dueDateInputToIso, formatDate, personName, toDateInput } from '../lib/format';
import { endOfSchoolYear, invalidateInventory, useBuildings } from '../lib/hooks';
import type { AssetListItem, Checkout, PersonSummary, Room } from '../lib/types';
import { alertGate, BorrowerAlertsPanel } from './borrower-alerts';
import { ASSIGNEE_OPTIONS, assigneeToInput, BuildingSelect, HomeLocationQuestion, PersonPicker, RoomPicker, useHomeLocationAnswer, type AssigneeValue } from './pickers';
import { useToast } from './toast';
import { Button, Card, CardHeader, cn, Field, Input, Spinner, Textarea } from './ui';

export type LookupAsset = Omit<AssetListItem, 'activeCheckout'> & { activeCheckout: Checkout | null };
type AssigneeType = AssigneeValue['type'];

export function DueDateField({ value, onChange }: { value: string; onChange(value: string): void }) {
  const { me } = useAuth();
  const days = me?.tenant?.defaultCheckoutDays ?? 14;
  return (
    <Field label="Due date">
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {[
          ['Tomorrow', toDateInput(addDays(1))],
          [`${days} days`, toDateInput(addDays(days))],
          ['End of year', toDateInput(endOfSchoolYear())],
          ['None', ''],
        ].map(([label, date]) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(date)}
            className={
              value === date
                ? 'rounded-full bg-brand-700 px-2.5 py-1 text-xs font-medium text-white'
                : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200'
            }
          >
            {label}
          </button>
        ))}
      </div>
    </Field>
  );
}

/** A scanned code on its way to becoming a device or a person. */
type Lookup<T> = { code: string; loading: true } | { code: string; loading: false; found: T } | { code: string; loading: false; error: string };

/** A check-out that couldn't go straight through: alerts on the borrower to read, or an error. */
interface Held {
  key: number;
  asset: LookupAsset;
  person: PersonSummary;
  alerts: BorrowerAlert[];
  error: string | null;
}

const found = <T,>(lookup: Lookup<T> | null) => (lookup && !lookup.loading && 'found' in lookup ? lookup.found : null);
const failed = (lookup: Lookup<unknown> | null) => (lookup && !lookup.loading && 'error' in lookup ? lookup.error : null);

/** Why a scanned device can't be checked out, if it can't. */
function unavailable(asset: LookupAsset) {
  if (asset.status === 'AVAILABLE') return null;
  if (asset.status === 'CHECKED_OUT') {
    return `${asset.assetTag} is checked out${asset.activeCheckout ? ` to ${asset.activeCheckout.assigneeLabel}` : ''}. Check it in first.`;
  }
  return `${asset.assetTag} is marked ${ASSET_STATUS_LABEL[asset.status].toLowerCase()}, so it can’t be checked out.`;
}

/**
 * Batch check out at the station. Who devices go to and when they're due are chosen once, before scanning, and stay for
 * every device. Going to people, each device is scanned and then the borrower's ID card, the way a barcode scanner reads
 * them, each scan ending with Enter. Going to a room, a building, or anything else, each device scanned goes out right away.
 */
export function BatchCheckout({ onCheckedOut }: { onCheckedOut(text: string): void }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const buildings = useBuildings();
  const deviceRef = useRef<HTMLInputElement>(null);
  const idRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<AssigneeType>('PERSON');
  const [room, setRoom] = useState<Room | null>(null);
  const [buildingId, setBuildingId] = useState('');
  const [label, setLabel] = useState('');
  const [dueDate, setDueDate] = useState(toDateInput(addDays(me?.tenant?.defaultCheckoutDays ?? 14)));
  const [notes, setNotes] = useState('');

  const [deviceCode, setDeviceCode] = useState('');
  const [idCode, setIdCode] = useState('');
  // What the two fields say about the latest scans.
  const [device, setDevice] = useState<Lookup<LookupAsset> | null>(null);
  const [idError, setIdError] = useState<string | null>(null);
  const [byName, setByName] = useState(false);
  // Pairs whose lookups are still going.
  const [pairing, setPairing] = useState<{ key: number; label: string }[]>([]);
  const [sending, setSending] = useState(0);
  const [held, setHeld] = useState<Held[]>([]);
  const nextKey = useRef(0);
  // The device waiting for its ID. An ID pairs with the device scanned just before it, at the moment it's scanned, however
  // long either lookup takes.
  const waiting = useRef<{ code: string; asset: Promise<LookupAsset> } | null>(null);

  // The same target for every device: a room, a building, or a label. People come from each scan.
  const target: AssigneeValue =
    type === 'ROOM' ? { type, room } : type === 'BUILDING' ? { type, buildingId } : type === 'OTHER' ? { type, label } : { type, person: null };
  const home = useHomeLocationAnswer(target);
  const targetReady = type === 'PERSON' || (!!assigneeToInput(target) && home.ready);
  const targetName =
    type === 'ROOM' ? room?.name : type === 'BUILDING' ? buildings.data?.find((b) => b.id === buildingId)?.name : type === 'OTHER' ? label.trim() : null;

  useEffect(() => {
    if (targetReady) setTimeout(() => deviceRef.current?.focus(), 0);
  }, [targetReady, type]);

  const startOver = () => {
    waiting.current = null;
    setDevice(null);
    setIdError(null);
    setDeviceCode('');
    setIdCode('');
    setByName(false);
    deviceRef.current?.focus();
  };

  const checkout = useMutation({
    mutationFn: ({ asset, assignee, override }: { asset: LookupAsset; assignee: AssigneeValue; person: PersonSummary | null; override: boolean; heldKey?: number }) =>
      api.post<{ checkouts: Checkout[]; home: HomeLocationResult | null }>('/checkouts', {
        assetIds: [asset.id],
        assignee: assigneeToInput(assignee),
        dueAt: dueDateInputToIso(dueDate),
        notes: notes.trim() || undefined,
        overrideAlerts: override,
        ...(assignee.type === 'ROOM' ? home.body() : {}),
      }),
    onMutate: () => setSending((count) => count + 1),
    onSettled: () => setSending((count) => count - 1),
    onSuccess: ({ checkouts, home: moved }, { heldKey }) => {
      invalidateInventory(queryClient);
      const out = checkouts[0];
      const due = out.dueAt ? ` (due ${formatDate(out.dueAt)})` : '';
      onCheckedOut(`${out.asset.assetTag} → ${out.assigneeLabel}${due}${homeLocationNote(moved)}`);
      toast.success(`Checked out ${out.asset.assetTag} to ${out.assigneeLabel}`);
      if (heldKey !== undefined) setHeld((rows) => rows.filter((row) => row.key !== heldKey));
    },
    onError: (err, { asset, person, heldKey }) => {
      invalidateInventory(queryClient);
      if (!person) {
        toast.error(`${asset.assetTag}: ${errorMessage(err)}`);
        return;
      }
      const alerts = alertsFromError(err) ?? [];
      const row = { asset, person, alerts, error: alerts.length ? null : errorMessage(err) };
      setHeld((rows) =>
        heldKey !== undefined ? rows.map((r) => (r.key === heldKey ? { ...r, ...row } : r)) : [{ key: nextKey.current++, ...row }, ...rows],
      );
    },
  });

  /** A person's check-out waits when they have alerts, so someone reads them first. */
  const sendToPerson = async (asset: LookupAsset, person: PersonSummary) => {
    let alerts: BorrowerAlert[] = [];
    try {
      alerts = (await api.get<{ alerts: BorrowerAlert[] }>(`/people/${person.id}/alerts`, { kind: 'STANDARD' })).alerts;
    } catch {
      // The check-out checks again, and says so if something blocks it.
    }
    if (alerts.length > 0) {
      setHeld((rows) => [{ key: nextKey.current++, asset, person, alerts, error: null }, ...rows]);
      return;
    }
    checkout.mutate({ asset, assignee: { type: 'PERSON', person }, person, override: false });
  };

  const lookUpAsset = async (code: string) => {
    const asset = await api.get<LookupAsset>('/assets/lookup', { q: code });
    const problem = unavailable(asset);
    if (problem) throw new Error(problem);
    return asset;
  };

  const submitDevice = () => {
    const code = deviceCode.trim();
    if (!code || !targetReady) return;
    setDeviceCode('');
    setIdError(null);
    setDevice({ code, loading: true });
    const lookup = lookUpAsset(code);
    const shown = (next: Lookup<LookupAsset> | null) => setDevice((current) => (current?.code === code ? next : current));

    if (type !== 'PERSON') {
      // Going to a room, a building, or a label, a scan is as good as a check-out.
      const assignee = target;
      lookup.then(
        (asset) => {
          shown(null);
          checkout.mutate({ asset, assignee, person: null, override: false });
        },
        (err) => shown({ code, loading: false, error: errorMessage(err) }),
      );
      return;
    }

    // The ID comes next, often straight from the scanner, so its field has to be ready before anything else happens.
    idRef.current?.focus();
    if (waiting.current) toast.error(`${waiting.current.code} wasn’t checked out: no ID was scanned for it.`);
    const entry = { code, asset: lookup };
    waiting.current = entry;
    lookup.then(
      (asset) => {
        if (waiting.current === entry) shown({ code, loading: false, found: asset });
      },
      (err) => {
        // Once an ID has taken the device, the pair reports the problem.
        if (waiting.current !== entry) return;
        waiting.current = null;
        shown({ code, loading: false, error: errorMessage(err) });
        // Back to the device, unless an ID is being scanned this moment.
        if (!idRef.current?.value) deviceRef.current?.focus();
      },
    );
  };

  /** Pairs a person with the device scanned just before them, and checks it out. */
  const pairWith = (label: string, person: Promise<PersonSummary>) => {
    const entry = waiting.current;
    if (!entry) {
      setIdError('Scan the device first, then the ID.');
      deviceRef.current?.focus();
      return;
    }
    waiting.current = null;
    // The next device follows.
    deviceRef.current?.focus();
    setByName(false);
    setIdError(null);
    setDevice(null);
    const key = nextKey.current++;
    setPairing((rows) => [...rows, { key, label: `${entry.code} → ${label}` }]);

    void Promise.allSettled([entry.asset, person]).then(async ([asset, found]) => {
      try {
        if (asset.status === 'rejected') {
          toast.error(`${entry.code} wasn’t checked out: ${errorMessage(asset.reason)}`);
          return;
        }
        if (found.status === 'rejected') {
          const error = errorMessage(found.reason);
          if (waiting.current) {
            // Another device has been scanned since, so this one has to be scanned again.
            toast.error(`${asset.value.assetTag} wasn’t checked out: ${error}`);
            return;
          }
          // The device waits for another try at the ID.
          waiting.current = entry;
          setDevice({ code: entry.code, loading: false, found: asset.value });
          setIdError(error);
          if (!deviceRef.current?.value) idRef.current?.focus();
          return;
        }
        await sendToPerson(asset.value, found.value);
      } finally {
        setPairing((rows) => rows.filter((row) => row.key !== key));
      }
    });
  };

  const submitId = () => {
    const code = idCode.trim();
    if (!code) return;
    setIdCode('');
    pairWith(code, api.get<PersonSummary>('/people/lookup', { q: code }));
  };

  /** Enter finishes a scan (scanners send it after the code); Esc starts the pair over. */
  const scanKeys = (submit: () => void) => (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      startOver();
    }
  };

  const chooseType = (next: AssigneeType) => {
    if (next === type) return;
    setType(next);
    startOver();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Before you scan" description="These stay the same for every device until you change them." />
        <div className="space-y-5 p-5">
          <Field as="div" label="Assign to" required>
            <div className="grid grid-cols-4 gap-1 rounded-lg bg-slate-100 p-1" role="radiogroup" aria-label="Assign to">
              {ASSIGNEE_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  role="radio"
                  aria-checked={type === option.type}
                  onClick={() => chooseType(option.type)}
                  className={cn(
                    'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                    type === option.type ? 'bg-white text-brand-800 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                  )}
                >
                  <option.icon className="size-3.5" />
                  {option.label}
                </button>
              ))}
            </div>
            <div className="mt-3">
              {type === 'PERSON' && <p className="text-sm text-slate-600">Scan each device, then the ID card of the person it goes to.</p>}
              {type === 'ROOM' && <RoomPicker inline value={room} onChange={setRoom} />}
              {type === 'BUILDING' && <BuildingSelect value={buildingId} onChange={setBuildingId} />}
              {type === 'OTHER' && (
                <Input value={label} maxLength={200} placeholder="e.g. Spring musical, Loaner pool, Department of Athletics" onChange={(event) => setLabel(event.target.value)} />
              )}
            </div>
          </Field>
          {home.room && <HomeLocationQuestion room={home.room} value={home.value} onChange={home.set} />}
          <div className="grid gap-5 sm:grid-cols-2">
            <DueDateField value={dueDate} onChange={setDueDate} />
            <Field label="Note" hint="Optional. Saved on every checkout.">
              <Textarea rows={2} maxLength={2000} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </Field>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Scan"
          description={
            type === 'PERSON'
              ? 'Scan a device, then an ID card. Each scan ends with Enter, which most barcode scanners send for you. Esc starts over.'
              : targetReady
                ? `Each device you scan is checked out to ${targetName} right away.`
                : type === 'ROOM' && room
                  ? 'Answer the home location question to start scanning.'
                  : `Choose ${type === 'ROOM' ? 'a room' : type === 'BUILDING' ? 'a building' : 'what devices go to'} to start scanning.`
          }
          actions={(sending > 0 || pairing.length > 0) && <Spinner className="size-4" />}
        />
        <div className={cn('grid gap-5 p-5', type === 'PERSON' && 'md:grid-cols-2')}>
          <ScanStep
            step={type === 'PERSON' ? 1 : undefined}
            label="Device"
            icon={<ScanBarcode />}
            status={
              device?.loading ? (
                <StepNote loading>Looking up {device.code}…</StepNote>
              ) : failed(device) ? (
                <StepNote error>{failed(device)}</StepNote>
              ) : found(device) ? (
                <StepNote>
                  <span className="font-mono font-semibold text-brand-800">{found(device)!.assetTag}</span> {found(device)!.name}
                  {type === 'PERSON' && ' - scan an ID next'}
                </StepNote>
              ) : null
            }
          >
            <Input
              ref={deviceRef}
              value={deviceCode}
              disabled={!targetReady}
              onChange={(event) => setDeviceCode(event.target.value)}
              onKeyDown={scanKeys(submitDevice)}
              placeholder="Scan or type an asset tag or serial"
              className="h-12 pl-11 font-mono text-base"
              autoComplete="off"
              aria-label="Device"
            />
          </ScanStep>

          {type === 'PERSON' && (
            <ScanStep
              step={2}
              label="Student or staff ID"
              icon={<IdCard />}
              status={idError && <StepNote error>{idError}</StepNote>}
              extra={
                byName ? (
                  <div className="space-y-1.5">
                    <PersonPicker
                      inline
                      autoFocus
                      value={null}
                      placeholder="Search by name"
                      onChange={(person) => person && pairWith(personName(person), Promise.resolve(person))}
                    />
                    <button type="button" onClick={() => setByName(false)} className="text-xs font-medium text-slate-500 hover:text-slate-800">
                      Scan an ID instead
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setByName(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
                  >
                    <Search className="size-3.5" /> No ID card? Find them by name
                  </button>
                )
              }
            >
              <Input
                ref={idRef}
                value={idCode}
                onChange={(event) => setIdCode(event.target.value)}
                onKeyDown={scanKeys(submitId)}
                placeholder="Scan or type an ID number or email"
                className="h-12 pl-11 font-mono text-base"
                autoComplete="off"
                aria-label="Student or staff ID"
              />
            </ScanStep>
          )}
        </div>
        {pairing.length > 0 && (
          <ul className="space-y-1 border-t border-slate-100 px-5 py-3 text-sm text-slate-600" aria-live="polite">
            {pairing.map((row) => (
              <li key={row.key} className="flex items-center gap-2">
                <Spinner className="size-4" /> Checking out {row.label}…
              </li>
            ))}
          </ul>
        )}
        {(device || idError) && (
          <div className="flex justify-end border-t border-slate-100 px-5 py-3">
            <Button size="sm" variant="ghost" icon={<X />} onClick={startOver}>
              Start over
            </Button>
          </div>
        )}
      </Card>

      {held.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader title="Needs attention" description="These didn’t go out yet. Keep scanning; come back to them when you can." />
          <ul className="divide-y divide-slate-100">
            {held.map((row) => (
              <HeldRow
                key={row.key}
                row={row}
                isAdmin={can('ADMIN')}
                sending={checkout.isPending && checkout.variables?.heldKey === row.key}
                onCheckOut={(override) =>
                  checkout.mutate({ asset: row.asset, assignee: { type: 'PERSON', person: row.person }, person: row.person, override, heldKey: row.key })
                }
                onDismiss={() => setHeld((rows) => rows.filter((r) => r.key !== row.key))}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ScanStep({
  step,
  label,
  icon,
  status,
  extra,
  children,
}: {
  step?: number;
  label: string;
  icon: ReactNode;
  status: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
        {step && <span className="flex size-5 items-center justify-center rounded-full bg-brand-700 text-xs text-white">{step}</span>}
        {label}
      </p>
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-slate-400 [&_svg]:size-5">{icon}</span>
        {children}
      </div>
      <div className="min-h-5">{status}</div>
      {extra}
    </div>
  );
}

function StepNote({ children, loading, error }: { children: ReactNode; loading?: boolean; error?: boolean }) {
  return (
    <p role={error ? 'alert' : 'status'} className={cn('flex items-start gap-1.5 text-sm', error ? 'text-red-700' : 'text-slate-600')}>
      {loading && <Spinner className="mt-0.5 size-4" />}
      {error && <CircleAlert className="mt-0.5 size-4 shrink-0" />}
      <span>{children}</span>
    </p>
  );
}

function HeldRow({
  row,
  isAdmin,
  sending,
  onCheckOut,
  onDismiss,
}: {
  row: Held;
  isAdmin: boolean;
  sending: boolean;
  onCheckOut(override: boolean): void;
  onDismiss(): void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const gate = alertGate(row.alerts, confirmed, isAdmin);
  return (
    <li className="space-y-3 px-5 py-4">
      <p className="text-sm text-slate-800">
        <span className="font-mono font-semibold text-brand-800">{row.asset.assetTag}</span> {row.asset.name} → <strong>{personName(row.person)}</strong>
      </p>
      {row.error && (
        <p role="alert" className="flex items-start gap-1.5 text-sm text-red-700">
          <CircleAlert className="mt-0.5 size-4 shrink-0" /> {row.error}
        </p>
      )}
      <BorrowerAlertsPanel alerts={row.alerts} confirmed={confirmed} onConfirm={setConfirmed} />
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDismiss} disabled={sending}>
          Don’t check out
        </Button>
        <Button size="sm" variant="primary" icon={<ArrowLeftRight />} loading={sending} disabled={!gate.ok} onClick={() => onCheckOut(gate.override)}>
          {row.error ? 'Try again' : 'Check out'}
        </Button>
      </div>
    </li>
  );
}
